// Galeria de cartas: filtros + grelha + carta ampliada. Partilhada pelo site (galeria pública)
// e pelo backoffice (página Cartas, W5). As imagens são sempre as exportadas pelo Godot (CardImage).
import { useTranslation } from "@ecliptica/i18n";
import { aspects } from "@ecliptica/tokens";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  EmptyState,
  Input,
  NativeSelect,
  SegmentedControl,
  cn,
} from "@ecliptica/ui";
import { useMemo, useState } from "react";
import { CardImage } from "./cards";
import {
  ASPECTS,
  CARD_TYPES,
  EMPTY_FILTER,
  RARITIES,
  filterCards,
  type Catalog,
  type CatalogCard,
  type CatalogLang,
  type CardFilter,
} from "./catalog";

const SYMBOL = { sol: "☀", lua: "☾", eclipse: "◐" } as const;

export function CardFilters({ catalog, lang, value, onChange }: { catalog: Catalog; lang: CatalogLang; value: CardFilter; onChange: (f: CardFilter) => void }) {
  const { t } = useTranslation("common");
  const set = (patch: Partial<CardFilter>) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-center justify-center gap-2.5" aria-label={t("gallery.label")}>
      <SegmentedControl
        label={t("gallery.label")}
        value={value.ed}
        onChange={(ed) => set({ ed })}
        options={[{ value: "", label: t("gallery.all") }, ...catalog.editions.map((e) => ({ value: e.code, label: e[lang] }))]}
      />
      <SegmentedControl
        label={t("gallery.label")}
        value={value.aspect}
        onChange={(aspect) => set({ aspect })}
        options={[
          { value: "", label: t("gallery.all") },
          ...ASPECTS.map((a) => ({
            value: a,
            label: (
              <>
                <b style={{ color: aspects[a] }}>{SYMBOL[a]}</b> {t(`game.aspect.${a}`)}
              </>
            ),
          })),
        ]}
      />
      <NativeSelect aria-label={t("gallery.allTypes")} value={value.type} onChange={(e) => set({ type: e.target.value })}>
        <option value="">{t("gallery.allTypes")}</option>
        {CARD_TYPES.map((k) => (
          <option key={k} value={k}>
            {t(`game.types.${k}`)}
          </option>
        ))}
      </NativeSelect>
      <NativeSelect aria-label={t("gallery.allRarities")} value={value.rarity} onChange={(e) => set({ rarity: e.target.value })}>
        <option value="">{t("gallery.allRarities")}</option>
        {RARITIES.map((k) => (
          <option key={k} value={k}>
            {t(`game.rarity.${k}`)}
          </option>
        ))}
      </NativeSelect>
      <Input type="search" className="w-full sm:w-60" placeholder={t("gallery.search")} aria-label={t("gallery.search")} value={value.q} onChange={(e) => set({ q: e.target.value })} />
    </div>
  );
}

export function CardGrid({ cards, lang, onOpen, className }: { cards: CatalogCard[]; lang: CatalogLang; onOpen: (c: CatalogCard) => void; className?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className={cn("grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4 sm:grid-cols-[repeat(auto-fill,minmax(170px,1fr))]", className)}>
      {cards.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onOpen(c)}
          aria-haspopup="dialog"
          aria-label={t("gallery.open", { name: c[lang].name, type: t(`game.type.${c.type}`), rarity: t(`game.rarity.${c.rarity}`) })}
          className="cursor-zoom-in rounded-[4.5%] transition duration-200 hover:-translate-y-1.5 hover:scale-[1.04] hover:drop-shadow-[0_10px_18px_rgba(0,0,0,.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {/* O nome acessível está no botão; a imagem leva o texto da carta (nome + efeito) para leitores de ecrã/imagem desligada. */}
          <CardImage id={c.id} lang={lang} alt={[c[lang].name, c[lang].text].filter(Boolean).join(". ")} width={300} height={420} />
        </button>
      ))}
    </div>
  );
}

export function CardDetail({ card, catalog, lang, onClose }: { card: CatalogCard | null; catalog: Catalog; lang: CatalogLang; onClose: () => void }) {
  const { t } = useTranslation("common");
  const ed = card && catalog.editions.find((e) => e.code === card.ed);
  const txt = card?.[lang];
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl border-none bg-transparent p-0 shadow-none [border-image:none] sm:grid-cols-[minmax(0,380px)_1fr] sm:items-center sm:gap-7">
        {card && txt && (
          <>
            <CardImage id={card.id} lang={lang} alt={txt.name} loading="eager" className="mx-auto w-[min(380px,80vw)]" />
            <div className="skin-panel rounded-lg border bg-popover p-5">
              <DialogTitle className="text-2xl">{txt.name}</DialogTitle>
              <DialogDescription className="mt-1">
                {t(`game.type.${card.type}`)}
                {txt.tribe && ` — ${txt.tribe}`} · {t(`game.rarity.${card.rarity}`)} · {ed ? ed[lang] : card.ed} {card.num}
              </DialogDescription>
              <p className="mt-3">{txt.text}</p>
              {txt.flavor && <p className="mt-3 italic text-mist">{txt.flavor}</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Galeria completa com estado próprio: mostra `pageSize` cartas e carrega mais a pedido. */
export function CardGallery({ catalog, lang, pageSize = 36 }: { catalog: Catalog; lang: CatalogLang; pageSize?: number }) {
  const { t } = useTranslation("common");
  const [filter, setFilter] = useState<CardFilter>(EMPTY_FILTER);
  const [shown, setShown] = useState(pageSize);
  const [open, setOpen] = useState<CatalogCard | null>(null);
  const list = useMemo(() => filterCards(catalog.cards, filter, lang), [catalog, filter, lang]);
  return (
    <>
      <CardFilters
        catalog={catalog}
        lang={lang}
        value={filter}
        onChange={(f) => {
          setFilter(f);
          setShown(pageSize);
        }}
      />
      <p className="my-4 text-center text-sm text-muted-foreground" aria-live="polite">
        {t("gallery.count", { count: list.length })}
      </p>
      {list.length ? (
        <CardGrid cards={list.slice(0, shown)} lang={lang} onOpen={setOpen} className="mx-auto max-w-[1300px]" />
      ) : (
        <EmptyState title={t("gallery.none")} className="mx-auto max-w-xl" />
      )}
      {list.length > shown && (
        <div className="mt-7 text-center">
          <Button onClick={() => setShown((n) => n + 48)}>{t("gallery.more")}</Button>
        </div>
      )}
      <CardDetail card={open} catalog={catalog} lang={lang} onClose={() => setOpen(null)} />
    </>
  );
}
